"use client"

import React, { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Box, Flex, Card, Heading, Text, TextField, Button, Separator, Callout, Dialog, RadioCards } from "@radix-ui/themes"
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  AlertCircle,
  CheckCircle,
  Store,
  MapPin
} from "lucide-react"
import { restaurantPortalAPI } from "../../services/restaurantApiClient"
import { useRestaurantAuth } from "../../contexts/RestaurantAuthContext"
import { useLanguage } from "../../contexts/LanguageContext"
import TurboLogo from "../../components/TurboLogo"
import LanguageSwitcher from "../../components/LanguageSwitcher"
import LoadingSpinner from "../../components/ui/dashboard/LoadingSpinner"

interface StaffLoginFormData {
  email: string
  password: string
}

interface RestaurantOption {
  id: string
  name: string
  address: {
    street: string
    city: string
  }
  role: string
}

const RestaurantStaffLoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, isAuthenticated, user, loading: authLoading } = useRestaurantAuth()
  const { t, language, dir } = useLanguage()
  const [formData, setFormData] = useState<StaffLoginFormData>({
    email: "",
    password: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Restaurant Selection State
  const [showSelectionModal, setShowSelectionModal] = useState(false)
  const [availableRestaurants, setAvailableRestaurants] = useState<RestaurantOption[]>([])
  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string>("")

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      if (user.restaurant?._id) {
        const role = user.role || '';
        let route = 'dashboard';
        if (role === 'cashier') route = 'orders';
        else if (role === 'kitchen_staff') route = 'kitchen-orders';
        else if (role === 'waiter') route = 'waiter-orders';

        navigate(`/restaurant/${user.restaurant._id}/${route}`)
      } else {
        // Fallback or error handling if restaurant ID is missing
        console.error("User authenticated but restaurant ID missing")
      }
    }
  }, [authLoading, isAuthenticated, user, navigate])

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Flex direction="column" align="center" justify="center" style={{ minHeight: "100vh", background: "linear-gradient(135deg, var(--red-2) 0%, var(--indigo-2) 100%)" }} dir={dir}>
        <LoadingSpinner size="3" text={t('checkingAuthentication')} />
      </Flex>
    )
  }

  // Don't render the form if already authenticated
  if (isAuthenticated && user) {
    return null
  }

  const handleLoginSuccess = (response: any) => {
    setSuccess(t('loginSuccessful'))

    // Transform staff user to match RestaurantUser interface
    const staffUser = response.data.user
    const transformedUser = {
      _id: staffUser._id,
      name: `${staffUser.firstName} ${staffUser.lastName}`,
      email: staffUser.email,
      phone: staffUser.phoneNumber,
      restaurant: staffUser.restaurant && typeof staffUser.restaurant === 'object'
        ? staffUser.restaurant
        : {
          _id: staffUser.restaurant,
          name: "",
          address: { street: "", city: "", state: "", zipCode: "" }
        },
      role: staffUser.role,
      isOwner: false,
      type: "restaurant_user",
      permissions: {
        manageMenu: staffUser.permissions?.canManageMenu || false,
        manageOrders: staffUser.permissions?.canProcessOrders || false,
        manageTables: staffUser.permissions?.canManageTables || false,
        viewAnalytics: staffUser.permissions?.canViewAnalytics || false,
        manageStaff: staffUser.permissions?.canManageUsers || false,
        canProcessOrders: staffUser.permissions?.canProcessOrders || false,
        canManageInventory: staffUser.permissions?.canManageInventory || false,
        canManageUsers: staffUser.permissions?.canManageUsers || false,
        canViewReports: staffUser.permissions?.canViewReports || false,
        canManageTables: staffUser.permissions?.canManageTables || false,
        canManageMenu: staffUser.permissions?.canManageMenu || false,
        canProcessRefunds: staffUser.permissions?.canProcessRefunds || false,
        canViewAnalytics: staffUser.permissions?.canViewAnalytics || false,
      }
    }

    // Use the restaurant auth context to login
    login(response.data.token, transformedUser)

    // Redirect to restaurant dashboard
    const restaurantId = transformedUser.restaurant._id || staffUser.restaurant
    const role = transformedUser.role || '';
    let route = 'dashboard';
    if (role === 'cashier') route = 'orders';
    else if (role === 'kitchen_staff') route = 'kitchen-orders';
    else if (role === 'waiter') route = 'waiter-orders';

    setTimeout(() => {
      navigate(`/restaurant/${restaurantId}/${route}`)
    }, 1000)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setSuccess("")

    try {
      // First attempt: Try login with just email/password
      const response = await restaurantPortalAPI.loginStaff({
        email: formData.email,
        password: formData.password,
        // No restaurantId initially
      })

      if (response.data.requiresSelection) {
        // Multiple restaurants found -> Show selection modal
        setAvailableRestaurants(response.data.restaurants)
        setShowSelectionModal(true)
        setLoading(false)
        return
      }

      handleLoginSuccess(response)

    } catch (error: any) {
      console.error("Staff login error:", error)
      const errorMessage = error?.response?.data?.message || error?.userMessage || error?.message || t('loginFailed')
      setError(errorMessage)
      setLoading(false)
    }
  }

  const handleRestaurantSelection = async () => {
    if (!selectedRestaurantId) return

    setLoading(true)
    setShowSelectionModal(false)

    try {
      // Second attempt: Login with selected restaurant ID
      const response = await restaurantPortalAPI.loginStaff({
        email: formData.email,
        password: formData.password,
        restaurantId: selectedRestaurantId
      })

      handleLoginSuccess(response)
    } catch (error: any) {
      console.error("Staff login error:", error)
      const errorMessage = error?.response?.data?.message || error?.userMessage || error?.message || t('loginFailed')
      setError(errorMessage)
      setLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
    <Flex direction="column" align="center" justify="center" p="4" style={{ minHeight: "100vh", background: "linear-gradient(135deg, var(--red-2) 0%, var(--indigo-2) 100%)" }} dir={dir}>
      {/* Language Switcher */}
      <Box style={{ position: "absolute", top: 16, [language === 'ar' ? 'left' : 'right']: 16 }}>
        <LanguageSwitcher />
      </Box>

      <Flex direction="column" gap="6" style={{ width: "100%", maxWidth: 448 }}>
        <Flex direction="column" align="center" gap="4">
          <Box>
            <TurboLogo width={64} height={54} color="#DC2626" />
          </Box>
          <Heading size="8" align="center">
            {t('staffLogin')}
          </Heading>
          <Text size="3" color="gray" align="center">
            {t('signInAsStaffMember')}
          </Text>
        </Flex>

        <Card size="4">
          {error && (
            <Callout.Root color="red" mb="4">
              <Callout.Icon>
                <AlertCircle size={20} />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </Callout.Root>
          )}

          {success && (
            <Callout.Root color="green" mb="4">
              <Callout.Icon>
                <CheckCircle size={20} />
              </Callout.Icon>
              <Callout.Text>{success}</Callout.Text>
            </Callout.Root>
          )}

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="4">
              {/* Restaurant ID field removed */}

              <Box>
                <Text as="label" size="2" weight="medium" mb="2">
                  {t('emailAddress')}
                </Text>
                <TextField.Root
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('enterYourEmail')}
                  size="3"
                >
                  <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                    <Mail size={18} />
                  </TextField.Slot>
                </TextField.Root>
              </Box>

              <Box>
                <Text as="label" size="2" weight="medium" mb="2">
                  {t('password')}
                </Text>
                <TextField.Root
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder={t('enterYourPassword')}
                  size="3"
                >
                  <TextField.Slot side={language === 'ar' ? 'right' : 'left'}>
                    <Lock size={18} />
                  </TextField.Slot>
                  <TextField.Slot side={language === 'ar' ? 'left' : 'right'}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </Button>
                  </TextField.Slot>
                </TextField.Root>
              </Box>

              <Button type="submit" disabled={loading} size="3" style={{ width: "100%" }}>
                {loading ? <LoadingSpinner size="1" /> : t('signIn')}
              </Button>
            </Flex>
          </form>

          <Separator size="4" my="4" />

          <Flex direction="column" align="center" gap="4">
            <Text size="2" color="gray">{t('restaurantOwner')}</Text>
            <Link to="/restaurant/login" style={{ color: "var(--red-9)", fontWeight: 500 }}>
              {t('loginAsOwner')}
            </Link>
          </Flex>
        </Card>

        <Flex justify="center">
          <Link to="/" style={{ fontSize: "var(--font-size-2)", color: "var(--gray-11)" }}>
            {language === 'ar' ? 'العودة للموقع الرئيسي ←' : '← Back to main site'}
          </Link>
        </Flex>
      </Flex>

      {/* Restaurant Selection Modal */}
      <Dialog.Root open={showSelectionModal} onOpenChange={setShowSelectionModal}>
        <Dialog.Content maxWidth="450px" dir={dir}>
          <Dialog.Title>Select Restaurant</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Your email is associated with multiple restaurants. Please select which one you want to log in to.
          </Dialog.Description>

          <RadioCards.Root
            value={selectedRestaurantId}
            onValueChange={setSelectedRestaurantId}
            columns={{ initial: "1", sm: "1" }}
            gap="2"
          >
            {availableRestaurants.map((restaurant) => (
              <RadioCards.Item key={restaurant.id} value={restaurant.id}>
                <Flex direction="column" width="100%">
                  <Flex align="center" gap="2" mb="1">
                    <Store size={16} />
                    <Text weight="bold">{restaurant.name}</Text>
                  </Flex>
                  <Flex align="center" gap="2">
                    <MapPin size={14} color="gray" />
                    <Text size="1" color="gray">
                      {restaurant.address?.city}, {restaurant.address?.street}
                    </Text>
                  </Flex>
                  <Text size="1" color="blue" mt="1" style={{ alignSelf: 'flex-start' }}>
                    Role: {restaurant.role}
                  </Text>
                </Flex>
              </RadioCards.Item>
            ))}
          </RadioCards.Root>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" onClick={() => setLoading(false)}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleRestaurantSelection} disabled={!selectedRestaurantId || loading}>
              {loading ? <LoadingSpinner size="1" /> : "Continue"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

    </Flex>
  )
}

export default RestaurantStaffLoginPage

